import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../api/AxiosConfig";
import { AppContext } from "../context/AppContext";
import { resolvePostLoginPath } from "../utils/postLoginRouting";
import styles from "./AppHub.module.css";
import permoniqLogo from "../img/logo-2.svg";
import permoniqHeatmapsLogo from "../img/logos/heatmaps_logo_left_inverse.svg";
import dataPlusLogo from "../img/logos/logo-dataplus.svg";
import cssPartnerLogo from "../img/logos/css_partner_logo.svg";
import ModalLoading from "../components/ModalLoading";
import ButtonBtn from "../components/buttons/ButtonBtn";

const AppHub = () => {
  const navigate = useNavigate();
  const { lng } = useParams();
  const { appContentType, isLogged, checkLoginStatus } = useContext(AppContext);

  useLayoutEffect(() => {
    const entryPoint = sessionStorage.getItem("Fx_0_0_m");

    if (entryPoint) {
      if (entryPoint === "p") {
        sessionStorage.removeItem("Fx_0_0_m");
        handleEnterApp();
      } else if (entryPoint === "hm") {
        sessionStorage.removeItem("Fx_0_0_m");
        handleEnterAddons();
      }
    }
  }, []);

  // Guard: only for logged-in users; otherwise redirect to login
  useEffect(() => {
    const ensureLogin = async () => {
      const ok = await checkLoginStatus();
      if (!ok) {
        navigate(`/${lng}/login/`, { replace: true });
      }
    };
    ensureLogin();
  }, [lng]);
  const [loading, setLoading] = useState(false);
  // Keep refs to buttons to measure their rendered widths and equalize
  const permoniqRef = useRef(null);
  const heatmapsRef = useRef(null);
  const dataPlusRef = useRef(null);
  const cssProgramRef = useRef(null);
  const buttonRefs = [permoniqRef, heatmapsRef, dataPlusRef, cssProgramRef];
  const [equalWidth, setEqualWidth] = useState(undefined);

  // Always start at top when this page mounts
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    // After mount and when loading changes (affects disabled state/width), compute max width
    const widths = buttonRefs
      .map((r) => r.current)
      .filter(Boolean)
      .map((el) => el.getBoundingClientRect().width);
    if (widths.length) {
      const max = Math.ceil(Math.max(...widths));
      if (!equalWidth || Math.abs(equalWidth - max) > 0.5) {
        setEqualWidth(max);
      }
    }
  }, [loading]);

  const handleEnterApp = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get("user/detail/");
      const dest = resolvePostLoginPath(data, {
        lng,
        appContentType,
        notFoundCookie: undefined,
      });
      if (dest) {
        navigate(dest);
      } else {
        // fallback – žádná jasná destinace
        navigate(`/${lng}/app/register-shop/`);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterAddons = () => {
    navigate(`/${lng}/tools/heatmaps/`);
  };

  return (
    <div className="body body--app">
      <section className="section">
        <article className="container">
          <div className={`${styles.wrap} ${styles.surface}`}>
            {/* <div className={styles.containerHeader}>
              <span className={styles.overline}>Rozcestník</span>
            </div> */}
            <div className={styles.duoGrid}>
              <div className={styles.column}>
                <div className={styles.columnHeader}>
                  <h2 className={styles.sectionTitle}>Aplikace</h2>
                </div>
                <div className={styles.panel}>
                  <div className={styles.panelHeader}>
                    <img
                      src={permoniqLogo}
                      alt="PermoniQ"
                      width="151"
                      height="32"
                      className={styles.cardLogo}
                    />
                  </div>
                  <div className={styles.actions}>
                    <ButtonBtn
                      ref={permoniqRef}
                      className={styles.primaryBtn}
                      onClick={handleEnterApp}
                      disabled={loading}
                      justifyContent="flex-start"
                      marginBlock="0"
                      style={equalWidth ? { width: equalWidth } : undefined}
                    >
                      PermoniQ
                    </ButtonBtn>
                  </div>
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.columnHeader}>
                  <h2 className={styles.sectionSubtitle}>Doplňky</h2>
                </div>
                <div className={styles.panel}>
                  <div className={styles.panelHeader}>
                    <img
                      src={permoniqHeatmapsLogo}
                      alt="PermoniQ Heatmapy"
                      width="151"
                      height="32"
                      className={styles.cardLogo}
                    />
                  </div>
                  <div className={styles.actions}>
                    <ButtonBtn
                      ref={heatmapsRef}
                      className={styles.primaryBtn}
                      onClick={handleEnterAddons}
                      justifyContent="flex-start"
                      marginBlock="0"
                      style={equalWidth ? { width: equalWidth } : undefined}
                    >
                      Heatmapy
                    </ButtonBtn>
                  </div>
                </div>

                <div className={styles.panel}>
                  <div className={styles.panelHeader}>
                    <img
                      src={dataPlusLogo}
                      alt="DataPlus"
                      width="151"
                      height="32"
                      className={styles.cardLogo}
                    />
                  </div>
                  <div className={styles.actions}>
                    {/* Using a button-styled anchor via ButtonBtn would require role=link; keep anchor for semantics but sync width using a sibling hidden measurer */}
                    <ButtonBtn
                      ref={dataPlusRef}
                      className={styles.primaryBtn}
                      as="a"
                      href="https://dataplus.advisio.cz/"
                      target="_blank"
                      rel="noopener noreferrer"
                      justifyContent="flex-start"
                      marginBlock="0"
                      style={equalWidth ? { width: equalWidth } : undefined}
                    >
                      DataPlus
                    </ButtonBtn>
                  </div>
                </div>

                <div className={styles.panel}>
                  <div className={styles.panelHeader}>
                    <img
                      src={cssPartnerLogo}
                      alt="Advisio CSS Program"
                      width="151"
                      height="32"
                      className={styles.cardLogo}
                    />
                  </div>
                  <div className={styles.actions}>
                    <ButtonBtn
                      ref={cssProgramRef}
                      className={styles.primaryBtn}
                      as="a"
                      href="https://www.advisio.cz/css-program/?utm_source=PermoniQ&utm_medium=referrer&utm_campaign=setup_section"
                      target="_blank"
                      rel="noopener noreferrer"
                      justifyContent="flex-start"
                      marginBlock="0"
                      style={equalWidth ? { width: equalWidth } : undefined}
                    >
                      CSS Program
                    </ButtonBtn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </section>
      {loading && <ModalLoading message="Načítám…" />}
    </div>
  );
};

export default AppHub;
